import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';

import { PageContainer } from '../components/layout/page-container';
import { PILL_TAB, PILL_TAB_OFF, PILL_TAB_ON } from '../components/ui/pill-tab';
import { cn } from '../lib/utils';
import { GatePrompt, useActionGate } from '../features/auth/action-gate';
import { useMe } from '../features/auth/use-me';
import { ProfilePage } from '../features/profile/profile-page';
import { BookmarkList } from '../features/bookmarks/bookmark-list';
import { MyArticleList } from '../features/articles/my-article-list';
import { CommunityFeed } from '../features/community/community-list';

/**
 * 마이 페이지 (/mypage). CSR, 로그인 전용. 가입은 병아리로 바로 되므로 게이트가 없다 — 여기가 회사명을
 * 적는 곳이기도 하다.
 */
export const Route = createFileRoute('/mypage')({
  // 로그인 화면 — 세션이 브라우저에만 있다. 서버는 레이아웃만 그린다(docs/SSR.md).
  ssr: false,
  component: MePage,
});

type Tab = 'profile' | 'bookmarks' | 'articles' | 'community';
const TABS: Array<{ key: Tab; label: string }> = [
  // 짧은 이름표 — 「나의 …」 넷이 모바일 한 줄을 넘쳤다 (2026-09-24 형).
  { key: 'profile', label: '내 프로필' },
  { key: 'bookmarks', label: '내 관심공고' },
  { key: 'articles', label: '내 아티클' },
  { key: 'community', label: '내 글' },
];

function MePage() {
  const gate = useActionGate();
  const { data: me } = useMe();
  const [tab, setTab] = useState<Tab>('profile');

  return (
    <PageContainer>
      {/* 이름은 모바일 머리와 알약 메뉴가 이미 말한다 — 화면 안에 또 쓰지 않는다. */}
      <h1 className="sr-only">마이 페이지</h1>
      {gate === 'anonymous' ? (
        <GatePrompt status={gate} message="마이 페이지를 보려면 로그인이 필요해요." />
      ) : me ? (
        <>
          {/* 알약 탭 — 사이트 한 벌(pill-tab). 프로필이 먼저 열린다. */}
          <div role="tablist" aria-label="마이 페이지" className="mb-4 flex items-center gap-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={cn(PILL_TAB, tab === t.key ? PILL_TAB_ON : PILL_TAB_OFF)}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          {tab === 'bookmarks' ? (
            <BookmarkList />
          ) : tab === 'articles' ? (
            <MyArticleList />
          ) : tab === 'community' ? (
            <CommunityFeed mine />
          ) : (
            <ProfilePage me={me} />
          )}
        </>
      ) : (
        <p className="text-sm text-text-muted">불러오는 중…</p>
      )}
    </PageContainer>
  );
}
